import { defineStore } from 'pinia';
import accountService from '@/services/accountService';
import ordersService from '@/services/ordersService';
import { useDemoFavoritesStore } from '@/stores/demoFavoritesStore';
import { getToken, getLanguage } from '@/utilities/cookies';
import { ref, computed } from 'vue';

export const useOrdersPageStore = defineStore('orderTemplates', () => {
  const orderTemplateData = ref([])
  const brands = ref([])
  const activeBrand = ref('')
  const showFavorites = ref(false)
  const itemsToShow = ref(50)
  const loading = ref(false)
  const apiError = ref(false)
  const token = getToken()
  const customerHomeBranch = ref('');

  const fetchOrderTemplateDetails = async (includeItems = true) => {
    const lang = getLanguage();
    try {
      loading.value = true;
      const data = await ordersService.fetchOrderTemplates(lang, includeItems);
      const categories = data?.Categories || [];
      const templates = [];
      brands.value = [];
      categories.forEach(category => {
        if (!brands.value.includes(category.CategoryName)) {
          brands.value.push(category.CategoryName);
        }
        category.Templates.forEach(template => {
          templates.push({
            id: template.TemplateId,
            brand: category.CategoryName,
            title: template.Name,
            description: template.Description,
            image: template.Image,
            fav: template.IsFavorite,
            favoritedAt: template.FavoritedAt || null,
            items: template.Items
          })
        })
      })
      orderTemplateData.value = templates
      if (brands.value.length > 0) {
        activeBrand.value = brands.value[0];
      }
    } catch (error) {
      console.error("Error fetching order templates data:", error);
      apiError.value = true;
    } finally {
      loading.value = false;
    }
  };

  const toggleFav = async (id) => {
    if (useDemoFavoritesStore().blockIfDemo()) return
    const card = orderTemplateData.value.find(c => c.id === id)
    if (!card) return
    const number = `CustomerTemplate_${id}`
    try {
      if (!card.fav) {
        const response = await accountService.addAsFavorite(number)
        if (response.data === true) {
          card.fav = true
          card.favoritedAt = Date.now()
        }
      } else {
        const response = await accountService.removeAsFavorite(number)
        if (response.data === true) {
          card.fav = false
          card.favoritedAt = null
        }
      }
    } catch (error) {
      console.error(`Failed to toggle favorite for ${number}:`, error);
      apiError.value = true;
    }
  }

  const loadMore = () => {
    itemsToShow.value += 50
  }

  const setBrand = (brand) => {
    activeBrand.value = brand
    itemsToShow.value = 50
  }

  const filteredCards = computed(() => {
    let cards = orderTemplateData.value.filter(card => card.brand === activeBrand.value)
    if (showFavorites.value) {
      cards = cards.filter(c => c.fav)
    }
    return cards
  })

  const visibleCards = computed(() => filteredCards.value.slice(0, itemsToShow.value))

  const getCustomerHomeBranchCode = async () => {
      try {
        const res = await accountService.fetchUserDetails(token);
        if (res.success) {
          customerHomeBranch.value = res.data?.HomeBranch;
        } else {
          throw new Error(res.error);
        }
      } catch (err) {
        console.error('Error fetching customer details:', err);
        apiError.value = true;
      }
    };

  const firstFiveCategoryCards = computed(() => {
  const result = [];
  const usedBrands = new Set();

    for (const template of orderTemplateData.value) {
      if (!usedBrands.has(template.brand)) {
        usedBrands.add(template.brand);
        result.push(template);
      }
      if (result.length === 5) return result;
    }

    for (const template of orderTemplateData.value) {
      if (result.length === 5) break;
      if (!result.includes(template)) {
        result.push(template);
      }
    }

    return result;
  });

  const favoriteTemplates = computed(() => {
    return orderTemplateData.value
      .filter(t => t.fav)
      .sort((a, b) => b.favoritedAt - a.favoritedAt)
  });


  return {
    orderTemplateData,
    brands,
    activeBrand,
    showFavorites,
    itemsToShow,
    fetchOrderTemplateDetails,
    toggleFav,
    loadMore,
    setBrand,
    getCustomerHomeBranchCode,
    filteredCards,
    visibleCards,
    loading,
    apiError,
    customerHomeBranch,
    firstFiveCategoryCards,
    favoriteTemplates
  }
})
